import _ from 'lodash'
import { createStore } from 'vuex'
import { autoreconcile, getFields } from '../api/index'
import { capitalize } from './../../../../../utils/string-helpers'
import * as mutationTypes from './mutation-types'

const generateFieldHeader = (field) => {
  let header = capitalize(field.name || field.identifier)

  let expectedValues

  if (field.expected_values) {
    if (field.expected_values[0] === 'boolean') {
      expectedValues = 'True, False'
    } else {
      expectedValues = field.expected_values.join(', ')
    }
  }

  if (expectedValues) header += ` (${expectedValues})`

  return header.trim()
}

const prepareAutoreconciliationFieldsCollection = (source, fields) => {
  const collections = {}
  const reconciliationFields = {}
  const reconciliationFieldsForSource = {}

  for (const collection in fields) {
    collections[collection] = fields[collection].import_title

    // Inputs can be empty
    const inputs = fields[collection].inputs || []

    inputs.forEach((reconciliationField) => {
      // identifiers are unique only per collection so we namespace them
      const identifier = collection + ':' + reconciliationField.identifier

      reconciliationFields[identifier] = {
        ...reconciliationField,
        identifier: identifier,
        original_identifier: reconciliationField.identifier,
        selected: '',
        header: generateFieldHeader(reconciliationField),
      }

      reconciliationFieldsForSource[collection] =
        reconciliationFieldsForSource[collection] || []
      reconciliationFieldsForSource[collection].push(identifier)
    })
  }

  return { collections, reconciliationFields, reconciliationFieldsForSource }
}

export default function createAutoReconcileStore(
  endpointParams,
  showLedgerSource,
  accept = '',
  autoreconciliationFields
) {
  const { collections, reconciliationFields, reconciliationFieldsForSource } =
    prepareAutoreconciliationFieldsCollection('', autoreconciliationFields)

  return createStore({
    state() {
      return {
        showLedgerSource: showLedgerSource,
        accept: accept,
        autoreconciliationFields,
        source: 'file',
        sourceFileId: undefined,
        collections,
        selectedCollection: Object.keys(collections)[0],
        reconciliationFields,
        reconciliationFieldsForSource,
        sourceFields: [],
        fieldsLoading: false,
        fieldsLoaded: showLedgerSource || false,
      }
    },

    getters: {
      reconciliationFieldsByCollection: (state) => (collection) => {
        if (!state.selectedCollection) {
          return []
        }
        return (state.reconciliationFieldsForSource[collection] || []).map(
          (identifier) => state.reconciliationFields[identifier]
        )
      },
    },

    mutations: {
      [mutationTypes.UPDATE_SOURCE](state, source) {
        state.source = source
      },

      [mutationTypes.SET_SOURCE_FILE_ID](state, fileId) {
        state.sourceFileId = fileId
      },

      [mutationTypes.SET_AUTORECONCILIATION_FIELDS](
        state,
        autoreconciliationFields
      ) {
        state.autoreconciliationFields = autoreconciliationFields
      },

      [mutationTypes.SET_COLLECTIONS](state, collections) {
        state.collections = collections
      },

      [mutationTypes.UPDATE_SELECTED_COLLECTION](state, collection) {
        state.selectedCollection = collection
      },

      [mutationTypes.SET_RECONCILIATION_FIELDS_FOR_SOURCE](
        state,
        reconciliationFieldsForSource
      ) {
        state.reconciliationFieldsForSource = reconciliationFieldsForSource
      },

      [mutationTypes.SET_RECONCILIATION_FIELDS](state, reconciliationFields) {
        state.reconciliationFields = reconciliationFields
      },

      [mutationTypes.UPDATE_RECONCILIATION_FIELD_SELECTION](
        state,
        { identifier, selected }
      ) {
        state.reconciliationFields = {
          ...state.reconciliationFields,
          [identifier]: {
            ...state.reconciliationFields[identifier],
            selected: selected,
          },
        }
      },

      [mutationTypes.UPDATE_RECONCILIATION_FIELD_ACCOUNTS](
        state,
        { identifier, accounts }
      ) {
        state.reconciliationFields[identifier].accounts = accounts
      },

      [mutationTypes.SET_SOURCE_FIELDS](state, sourceFields) {
        state.sourceFields = sourceFields
      },

      [mutationTypes.UPDATE_FIELDS_LOADING](state, fieldsLoading) {
        state.fieldsLoading = fieldsLoading
      },
      [mutationTypes.UPDATE_FIELDS_LOADED](state, fieldsLoaded) {
        state.fieldsLoaded = fieldsLoaded
      },
    },

    actions: {
      updateSource({ commit, dispatch }, { source, optionalParams }) {
        commit(mutationTypes.UPDATE_SOURCE, source)
        dispatch('fetchFieldsBySource', { optionalParams: optionalParams })
      },

      updateReconciliationFieldSelection({ commit }, { identifier, selected }) {
        commit(mutationTypes.UPDATE_RECONCILIATION_FIELD_SELECTION, {
          identifier: identifier,
          selected: selected,
        })
      },

      updateReconciliationFieldAccounts({ commit }, { identifier, accounts }) {
        commit(mutationTypes.UPDATE_RECONCILIATION_FIELD_ACCOUNTS, {
          identifier,
          accounts,
        })
      },

      updateSelectedCollection({ commit }, collection) {
        commit(mutationTypes.UPDATE_SELECTED_COLLECTION, collection)
      },

      updateAutoreconciliationFields(
        { commit, dispatch, state },
        autoreconciliationFields
      ) {
        const previousFields = Object.values(state.reconciliationFields)

        commit(
          mutationTypes.SET_AUTORECONCILIATION_FIELDS,
          autoreconciliationFields
        )
        dispatch('resetCollectionsAndReconciliationFields')

        if (!(state.selectedCollection in state.collections)) {
          commit(
            mutationTypes.UPDATE_SELECTED_COLLECTION,
            Object.keys(state.collections)[0]
          )
        }

        // The modal's dropdowns and account radios are not bound to the store,
        // so a field that still exists keeps exactly what the user picked.
        dispatch('updateReconciliationFieldsSelections')
        previousFields
          .filter(({ identifier }) => state.reconciliationFields[identifier])
          .forEach(({ identifier, selected, accounts }) => {
            commit(mutationTypes.UPDATE_RECONCILIATION_FIELD_SELECTION, {
              identifier,
              selected,
            })
            if (accounts) {
              commit(mutationTypes.UPDATE_RECONCILIATION_FIELD_ACCOUNTS, {
                identifier,
                accounts,
              })
            }
          })
      },

      updateFieldsLoading({ commit }, fieldsLoading) {
        commit(mutationTypes.UPDATE_FIELDS_LOADING, fieldsLoading)
      },

      resetCollectionsAndReconciliationFields({ commit, state }) {
        const fields = _.cloneDeep(state.autoreconciliationFields)

        const {
          collections,
          reconciliationFields,
          reconciliationFieldsForSource,
        } = prepareAutoreconciliationFieldsCollection(state.source, fields)

        commit(mutationTypes.SET_COLLECTIONS, collections)
        commit(
          mutationTypes.SET_RECONCILIATION_FIELDS_FOR_SOURCE,
          reconciliationFieldsForSource
        )
        commit(mutationTypes.SET_RECONCILIATION_FIELDS, reconciliationFields)
      },

      resetSourceFields({ commit, dispatch }, { fields }) {
        const sourceFields = fields.map((option) => {
          const [name, value] = option

          return { name, value }
        })

        sourceFields.unshift({
          name: I18n.t('autoreconciliation.dont_select'),
          value: '',
        })

        commit(mutationTypes.SET_SOURCE_FIELDS, sourceFields)
        dispatch('updateReconciliationFieldsSelections')
      },

      updateReconciliationFieldsSelections({ state, commit }) {
        if (!state.selectedCollection) {
          return
        }

        state.sourceFields.forEach((sourceField) => {
          const { name, value } = sourceField

          if (!name) {
            return
          }

          const selectedFieldsIdentifiers =
            state.reconciliationFieldsForSource[state.selectedCollection] || []
          // Must match how fields are selected in the View
          const identifier = selectedFieldsIdentifiers.find(
            (identifier) =>
              state.reconciliationFields[identifier] &&
              state.reconciliationFields[identifier].name.toLowerCase() ===
                name.trim().toLowerCase()
          )

          if (identifier) {
            commit(mutationTypes.UPDATE_RECONCILIATION_FIELD_SELECTION, {
              identifier,
              selected: value,
            })
          }
        })
      },

      resetSelectedCollection({ commit, state }) {
        if (Object.keys(state.collections).length > 0) {
          if (state.selectedCollection !== '') {
            // if selected, keep same
            commit(
              mutationTypes.UPDATE_SELECTED_COLLECTION,
              state.selectedCollection
            )
          } else {
            // if not selected (loaded first time), select first
            commit(
              mutationTypes.UPDATE_SELECTED_COLLECTION,
              Object.keys(state.collections)[0]
            )
          }
        }
      },

      resetFields({ commit, dispatch }, { response }) {
        dispatch('resetCollectionsAndReconciliationFields')
        dispatch('resetSelectedCollection')
        dispatch('resetSourceFields', {
          fields: response.source_fields,
        })
        commit(mutationTypes.UPDATE_FIELDS_LOADING, false)
      },

      fetchFieldsBySource({ state, dispatch }, { optionalParams }) {
        switch (state.source) {
          case 'ledger':
            dispatch('fetchFields', {
              sourceParams: {
                type: state.source,
              },
            })
            break
          case 'file':
            dispatch('fetchFields', {
              sourceParams: {
                type: state.source,
                blob_signed_id: optionalParams.blob_signed_id,
                path: optionalParams.filePath,
              },
            })
            break
        }
      },

      async fetchFields({ commit, dispatch }, { sourceParams }) {
        commit(mutationTypes.UPDATE_FIELDS_LOADING, true)

        try {
          const response = await getFields(endpointParams, sourceParams)

          switch (sourceParams.type) {
            case 'ledger':
              dispatch('resetFields', { response: response })
              break
            case 'file':
              if (response.account_entry_id) {
                // When account entry is created at the moment of import file upload
                endpointParams.accountEntryId = response.account_entry_id
              }
              commit(mutationTypes.SET_SOURCE_FILE_ID, response.file_id)
              // once file got processed we can fetch headers
              window.Silverfin.Polling.subscribe(
                '/backgrounds/completed',
                (data) => {
                  if (data === response.context) {
                    dispatch('fetchFields', {
                      sourceParams: {
                        type: 'headers',
                        file_id: response.file_id,
                      },
                    })
                  }
                },
                response.last_id
              )
              break
            // from file headers
            case 'headers':
              dispatch('resetFields', { response: response })
              break
          }
        } catch (error) {
          if (error.responseJSON.type === 'file') {
            // if file processing errored out e.g.
            // "Validation failed: Csv file name is invalid, Csv is invalid"
            // we switch back to From Ledger
            commit(mutationTypes.UPDATE_SOURCE, 'ledger')
          }
          commit(mutationTypes.UPDATE_FIELDS_LOADING, false)
          alert('Something went wrong: ' + error.responseJSON.error)
        } finally {
          commit(mutationTypes.UPDATE_FIELDS_LOADED, true)
        }
      },

      async submitFields({ state, getters }, { method }) {
        const reconciliationFields = getters.reconciliationFieldsByCollection(
          state.selectedCollection
        )
        const params = {
          source: state.source,
          file_id: state.sourceFileId,
          collection: state.selectedCollection,
          method: method,
          mapping: {},
        }

        for (const identifier in reconciliationFields) {
          const reconciliationField = reconciliationFields[identifier]
          params.mapping[reconciliationField.original_identifier] =
            reconciliationField.selected
          if (reconciliationField.accounts) {
            params.accounts ||= {}
            params.accounts[reconciliationField.original_identifier] =
              reconciliationField.accounts
          }
        }

        return autoreconcile(endpointParams, params)
      },
    },
  })
}
